import axios from 'axios';
import { Campaign } from '../types/marketing';
import { NewLead } from './whatsappService';

export interface EmailConfig {
  smtpServer: string;
  smtpPort: number;
  emailUser: string;
  emailPassword: string;
  useSSL: boolean;
}

export interface EmailMessage {
  id: string;
  subject: string;
  body: string;
  to: string;
  from: string;
  date: Date;
  read: boolean;
  replied: boolean;
  leadId?: string;
  trackingId?: string; // ID único para rastreamento
  readTimestamp?: Date; // Timestamp de quando o e-mail foi lido
  status?: string; // Status do e-mail (sent, delivered, opened, etc.)
}

export interface EmailTemplate {
  id: string;
  name: string;
  subject: string;
  content: string;
  variables: string[];
}

export interface EmailReadEvent {
  trackingId: string;
  email: string;
  timestamp: Date;
  userAgent?: string;
  ipAddress?: string;
}

// Enum para os status de e-mail
export enum EmailStatus {
  SENT = 'sent',           // Enviado
  DELIVERED = 'delivered', // Entregue
  OPENED = 'opened',       // Aberto (lido)
  UNREAD = 'unread',       // Não lido (entregue mas não aberto)
  REPLIED = 'replied',     // Respondido
  BOUNCED = 'bounced',     // Rejeitado (bounce)
  DISABLED = 'disabled',   // Desativado (e-mail inválido ou inativo)
  FAILED = 'failed'        // Não entregue (falha no envio)
}

export interface EmailStatusEvent {
  status: EmailStatus;
  timestamp: Date;
  details: any;
}

export interface EmailHistoryItem {
  _id: string;
  messageId: string;
  trackingId?: string;
  from: string;
  to: string;
  subject: string;
  content?: string;
  sentAt: Date;
  currentStatus: EmailStatus;
  statusHistory: EmailStatusEvent[];
  leadId?: any;
  metadata?: any;
  createdAt: Date;
  updatedAt: Date;
}

// Interface para contagem de e-mails por status
export interface EmailCounts extends Record<EmailStatus, number> {
  total: number;
}

export interface EmailStatistics {
  counts: EmailCounts;
  rates: {
    openRate: number;
    bounceRate: number;
    replyRate: number;
    deliveryRate: number;
  };
}

const API_URL = 'http://localhost:3001/api/email';

export const emailService = {
  async saveEmailConfig(config: EmailConfig) {
    const response = await axios.post(`${API_URL}/email/config`, config);
    return response.data;
  },

  async testEmailConfig(config: EmailConfig) {
    const response = await axios.post(`${API_URL}/email/test`, config);
    return response.data;
  },

  async getEmailConfig() {
    const response = await axios.get(`${API_URL}/email/config`);
    return response.data;
  },

  // Função para gerar um ID de rastreamento único
  generateTrackingId(): string {
    return `trk_${Date.now()}_${Math.random().toString(36).substring(2, 15)}`;
  },

  // Função para adicionar pixel de rastreamento ao conteúdo HTML
  addTrackingPixel(content: string, trackingId: string): string {
    // Verificar se o conteúdo é HTML
    const isHtml = content.includes('<html') || content.includes('<body') || content.includes('<div');
    
    if (isHtml) {
      // Se for HTML, adicionar o pixel antes do fechamento do body ou html
      const trackingPixel = `<img src="${API_URL}/tracking/${trackingId}" width="1" height="1" style="display:none" alt="" />`;
      
      if (content.includes('</body>')) {
        return content.replace('</body>', `${trackingPixel}</body>`);
      } else if (content.includes('</html>')) {
        return content.replace('</html>', `${trackingPixel}</html>`);
      } else {
        // Se não encontrar body ou html, adicionar no final
        return `${content}${trackingPixel}`;
      }
    } else {
      // Se não for HTML, converter para HTML simples e adicionar o pixel
      return `<html><body>${content}<img src="${API_URL}/tracking/${trackingId}" width="1" height="1" style="display:none" alt="" /></body></html>`;
    }
  },

  async sendEmail(to: string, subject: string, content: string, enableTracking: boolean = true, leadId?: string): Promise<{ messageId: string, trackingId?: string }> {
    let trackingId;
    let processedContent = content;
    
    // Se o rastreamento estiver habilitado, adicionar o pixel
    if (enableTracking) {
      trackingId = this.generateTrackingId();
      processedContent = this.addTrackingPixel(content, trackingId);
    }
    
    const response = await fetch(`${API_URL}/send`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        to,
        subject,
        content: processedContent,
        trackingId,
        leadId
      }),
    });
    
    if (!response.ok) {
      throw new Error('Erro ao enviar e-mail');
    }
    
    const data = await response.json();
    return { 
      messageId: data.messageId,
      trackingId
    };
  },

  async sendCampaign(campaign: Campaign): Promise<void> {
    if (campaign.type !== 'email') {
      throw new Error('Campanha não é do tipo e-mail');
    }

    if (!campaign.subject) {
      throw new Error('Campanha de e-mail deve ter um assunto');
    }

    for (const recipient of campaign.recipients) {
      try {
        await this.sendEmail(recipient, campaign.subject, campaign.content);
      } catch (error) {
        console.error(`Erro ao enviar e-mail para ${recipient}:`, error);
        // Continua para o próximo destinatário mesmo se houver erro
      }
    }
  },

  async getEmailStatus(messageId: string): Promise<{
    status: 'sent' | 'delivered' | 'opened' | 'clicked' | 'failed';
    timestamp: string;
  }> {
    const response = await fetch(`${API_URL}/messages/${messageId}/status`);
    if (!response.ok) {
      throw new Error('Erro ao buscar status do e-mail');
    }
    return response.json();
  },

  // Função para registrar eventos de leitura de e-mail
  async registerEmailRead(trackingId: string): Promise<EmailReadEvent> {
    const response = await fetch(`${API_URL}/tracking/register`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        trackingId
      }),
    });
    
    if (!response.ok) {
      throw new Error('Erro ao registrar leitura de e-mail');
    }
    
    return response.json();
  },

  // Função para obter eventos de leitura de e-mail
  async getEmailReadEvents(trackingId?: string): Promise<EmailReadEvent[]> {
    const url = trackingId 
      ? `${API_URL}/tracking/events/${trackingId}` 
      : `${API_URL}/tracking/events`;
      
    const response = await fetch(url);
    
    if (!response.ok) {
      throw new Error('Erro ao buscar eventos de leitura de e-mail');
    }
    
    return response.json();
  },

  // Métodos para o sistema de monitoramento de e-mails
  async getEmailHistory(page: number = 1, limit: number = 20, filters: { status?: EmailStatus, recipient?: string, leadId?: string } = {}): Promise<{ emails: EmailHistoryItem[], total: number, page: number, limit: number, totalPages: number }> {
    // Construir parâmetros de consulta
    const params = new URLSearchParams();
    params.append('page', page.toString());
    params.append('limit', limit.toString());
    
    if (filters.status) {
      params.append('status', filters.status);
    }
    
    if (filters.recipient) {
      params.append('recipient', filters.recipient);
    }
    
    if (filters.leadId) {
      params.append('leadId', filters.leadId);
    }
    
    const response = await fetch(`${API_URL}/history?${params.toString()}`);
    
    if (!response.ok) {
      throw new Error('Erro ao buscar histórico de e-mails');
    }
    
    const data = await response.json();
    
    if (!data.success) {
      throw new Error(data.message || 'Erro ao buscar histórico de e-mails');
    }
    
    return data.data;
  },

  async getEmailDetails(messageId: string): Promise<EmailHistoryItem> {
    const response = await fetch(`${API_URL}/history/${messageId}`);
    
    if (!response.ok) {
      throw new Error('Erro ao buscar detalhes do e-mail');
    }
    
    const data = await response.json();
    
    if (!data.success) {
      throw new Error(data.message || 'Erro ao buscar detalhes do e-mail');
    }
    
    return data.data;
  },

  async getEmailStatistics(): Promise<EmailStatistics> {
    const response = await fetch(`${API_URL}/statistics`);
    
    if (!response.ok) {
      throw new Error('Erro ao buscar estatísticas de e-mails');
    }
    
    const data = await response.json();
    
    if (!data.success) {
      throw new Error(data.message || 'Erro ao buscar estatísticas de e-mails');
    }
    
    return data.data;
  },

  async registerBounce(messageId: string, reason: string, category?: string, diagnosticCode?: string): Promise<void> {
    const response = await fetch(`${API_URL}/status/bounce`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        messageId,
        reason,
        category,
        diagnosticCode
      }),
    });
    
    if (!response.ok) {
      throw new Error('Erro ao registrar bounce de e-mail');
    }
  },

  async registerReplied(messageId: string, replyMessageId?: string, replyContent?: string): Promise<void> {
    const response = await fetch(`${API_URL}/status/replied`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        messageId,
        replyMessageId,
        replyContent
      }),
    });
    
    if (!response.ok) {
      throw new Error('Erro ao registrar resposta de e-mail');
    }
  },

  async registerDisabled(messageId: string, reason: string): Promise<void> {
    const response = await fetch(`${API_URL}/status/disabled`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        messageId,
        reason
      }),
    });
    
    if (!response.ok) {
      throw new Error('Erro ao registrar e-mail como desativado');
    }
  },

  async getTemplates(): Promise<{
    id: string;
    name: string;
    subject: string;
    content: string;
  }[]> {
    const response = await fetch(`${API_URL}/templates`);
    if (!response.ok) {
      throw new Error('Erro ao buscar templates de e-mail');
    }
    return response.json();
  },

  async saveTemplate(template: {
    name: string;
    subject: string;
    content: string;
  }): Promise<{
    id: string;
    name: string;
    subject: string;
    content: string;
  }> {
    const response = await fetch(`${API_URL}/templates`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(template),
    });
    if (!response.ok) {
      throw new Error('Erro ao salvar template de e-mail');
    }
    return response.json();
  },

  async detectEmailInMessage(message: string): Promise<string | null> {
    // Regex para detectar padrões de e-mail
    const emailRegex = /\b[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Z|a-z]{2,}\b/g;
    
    // Encontrar todos os possíveis e-mails na mensagem
    const matches = message.match(emailRegex);
    
    // Retornar o primeiro e-mail encontrado ou null se não houver
    return matches ? matches[0] : null;
  },

  async detectNameInMessage(message: string): Promise<string | null> {
    // Tentar encontrar um nome próprio no formato "Nome Sobrenome"
    const nameRegex = /([A-Z][a-z]+ [A-Z][a-z]+)/;
    const matches = message.match(nameRegex);
    
    return matches ? matches[1] : null;
  },

  async createLeadFromEmail(email: EmailMessage): Promise<NewLead> {
    try {
      // Extrair e-mail do remetente
      const emailAddress = email.from;
      
      // Tentar extrair nome do corpo da mensagem ou usar o e-mail como fallback
      const detectedName = await this.detectNameInMessage(email.body);
      const name = detectedName || emailAddress.split('@')[0];
      
      // Criar objeto de lead
      const newLead: NewLead = {
        name: name,
        email: emailAddress,
        phoneNumber: '', // Inicialmente vazio, pode ser preenchido posteriormente
        source: 'E-mail',
        notes: `Lead gerado automaticamente a partir de e-mail recebido em ${new Date().toLocaleString()}\nAssunto: ${email.subject}`,
        tags: ['email', 'auto-generated']
      };
      
      return newLead;
    } catch (error) {
      console.error('Erro ao criar lead a partir do e-mail:', error);
      throw new Error('Não foi possível criar o lead a partir do e-mail');
    }
  },

  async updateLeadWithEmailInfo(emailMessage: EmailMessage): Promise<void> {
    try {
      // Importar o serviço de leads dinamicamente para evitar dependência circular
      const { leadService } = await import('./leadService');
      
      // Criar um objeto de lead com as informações do e-mail
      const leadData = await this.createLeadFromEmail(emailMessage);
      
      // Atualizar ou criar o lead
      await leadService.createLeadFromEmail(leadData, emailMessage.id);
      
      console.log(`Lead atualizado/criado a partir do e-mail: ${emailMessage.from}`);
    } catch (error) {
      console.error('Erro ao atualizar lead com informações do e-mail:', error);
    }
  },

  async getInbox(limit: number = 20): Promise<EmailMessage[]> {
    const response = await fetch(`${API_URL}/inbox?limit=${limit}`);
    if (!response.ok) {
      throw new Error('Erro ao buscar e-mails da caixa de entrada');
    }
    return response.json();
  },

  async markAsRead(messageId: string): Promise<void> {
    const response = await fetch(`${API_URL}/messages/${messageId}/read`, {
      method: 'PATCH'
    });
    if (!response.ok) {
      throw new Error('Erro ao marcar e-mail como lido');
    }
  },

  async replyToEmail(messageId: string, content: string, subject?: string): Promise<void> {
    const response = await fetch(`${API_URL}/messages/${messageId}/reply`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        content,
        subject
      }),
    });
    if (!response.ok) {
      throw new Error('Erro ao responder e-mail');
    }
  },

  async saveEmailTemplate(template: Omit<EmailTemplate, 'id'>): Promise<EmailTemplate> {
    const response = await fetch(`${API_URL}/templates`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(template),
    });
    if (!response.ok) {
      throw new Error('Erro ao salvar template de e-mail');
    }
    return response.json();
  }
};